import api from './api';
import { ReferralStats, ReferralResponse, ReferralStatsResponse, LoggerService, ReferralWithUser } from '../types/referral';
import TelegramWebApp from '../telegram-init';

class ReferralService {
  private logger: LoggerService = {
    debug: (...args: unknown[]) => console.debug('[ReferralService]', ...args),
    error: (...args: unknown[]) => console.error('[ReferralService]', ...args)
  };

  async getReferralStats(userId: string): Promise<ReferralStats> {
    try {
      this.logger.debug('Getting referral stats for user:', userId);
      const response = await api.get<ReferralStatsResponse>(`/api/v1/referrals/stats/${userId}`);
      this.logger.debug('Full response:', response);
      this.logger.debug('Response data:', response.data);
      this.logger.debug('Response data type:', typeof response.data);
      this.logger.debug('Response data keys:', Object.keys(response.data));
      return response.data as unknown as ReferralStats;
    } catch (error) {
      this.logger.error('Error getting referral stats:', error);
      throw error;
    }
  }

  async createReferral(referralCode: string): Promise<ReferralResponse> {
    try {
      this.logger.debug('Creating referral with code:', referralCode);
      const response = await api.post<ReferralResponse>('/api/v1/referrals', {
        referral_code: referralCode
      });
      this.logger.debug('Referral created:', response.data);
      return response.data;
    } catch (error) {
      this.logger.error('Error creating referral:', error);
      throw error;
    }
  }

  async generateReferralCode(): Promise<string> {
    try {
      this.logger.debug('Generating referral code');
      const response = await api.post<{ referral_code: string }>('/api/v1/referrals/generate-code');
      this.logger.debug('Generated referral code:', response.data.referral_code);
      return response.data.referral_code;
    } catch (error) {
      this.logger.error('Error generating referral code:', error);
      throw error;
    }
  }

  async copyReferralLink(referralCode: string): Promise<void> {
    try {
      const referralLink = `https://t.me/BigDealAppBot?start=${referralCode}`;
      
      // Создаем временный input элемент
      const tempInput = document.createElement('input');
      tempInput.value = referralLink;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);
      
      // Тактильный отклик
      TelegramWebApp.HapticFeedback.impactOccurred('medium');
      
      // Показываем уведомление об успешном копировании
      TelegramWebApp.showPopup({
        title: 'Success',
        message: `Referral link copied to clipboard!\n\n${referralLink}\n\nShare it with your friends to earn points.`,
        buttons: [{ type: 'default', text: 'OK' }]
      });
    } catch (error) {
      this.logger.error('Error copying referral link:', error);
      TelegramWebApp.showPopup({
        title: 'Error',
        message: 'Failed to copy referral link',
        buttons: [{ type: 'default', text: 'OK' }]
      });
      throw error;
    }
  }

  async getReferralCount(userId: string): Promise<number> {
    try {
      this.logger.debug('Getting referral count for user:', userId);
      const response = await api.get<number>(`/api/v1/referrals/count/${userId}`);
      this.logger.debug('Received referral count:', response.data);
      return response.data;
    } catch (error) {
      this.logger.error('Error getting referral count:', error);
      throw error;
    }
  }

  async updateReferralStatus(referralId: string, purchased: boolean): Promise<ReferralResponse> {
    try {
      this.logger.debug('Updating referral status:', { referralId, purchased });
      const response = await api.put<ReferralResponse>(`/api/v1/referrals/${referralId}/subscription`, {
        purchased
      });
      this.logger.debug('Referral status updated:', response.data);
      return response.data;
    } catch (error) {
      this.logger.error('Error updating referral status:', error);
      throw error;
    }
  }

  async getReferrals(userId: string): Promise<ReferralWithUser[]> {
    try {
      this.logger.debug('Getting referrals for user:', userId);
      const response = await api.get<ReferralWithUser[]>(`/api/v1/referrals/list`);
      this.logger.debug('Received referrals:', response.data);
      return response.data;
    } catch (error) {
      this.logger.error('Error getting referrals:', error);
      throw error;
    }
  }
}

export const referralService = new ReferralService(); 